import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { gridItem, property } from 'src/app/models';
import { State } from '../../state';
import * as mainActions from 'src/app/state/app.actions'
import { ErrorMessage } from 'src/app/common/messages';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-relative-date-select',
    templateUrl: './relative-date-select.component.html',
    styleUrls: ['./relative-date-select.component.scss'],
    standalone: false
})
export class RelativeDateSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public gridItem: gridItem;
  @Input() public disabled: boolean;
  @Input() public model: string;
  @Input() public completed: boolean;
  @Input() public reportMaxDays: number;
  @Input() public notifier: Subject<any>;
  @Input() public isExportDialog: boolean;
  @Output() addToDashboardChange: EventEmitter<any> = new EventEmitter();
  @Output() completedChange = new EventEmitter<boolean>();

  ngUnsubscribe = new Subject<void>();
  oppositeModel: string = "";
  dayTypesSelectOpened: boolean = false;
  weekDaysSelectOpened: boolean = false;
  monthsSelectOpened: boolean = false;
  disableValueSelect: boolean = false;
  valuesList: property[] = [];
  dayTypes: property[] = ["Today", "Yesterday", "This week", "Last week", "This month", "Last month", "January this year"].map((m, i) => <property>{ id: i, name: m });
  weekDays: property[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((m, i) => <property>{ id: i, name: m });
  months: property[] = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => <property>{ id: i, name: m });

  constructor(private store: Store<State>, private i18nextPipe: I18NextPipe) { }

  SelectDayTypesChange($event: property) {
    this.gridItem.setup.period[this.model + 'Value'] = null;
    this.SetValueSelct($event.id);
  }

  SetValueSelct(id: number) {
    let isPeriodCorrect = this.checkPeriodLength();
    this.disableValueSelect = id < 2;
    switch (id) {
      case 2:
      case 3:
        this.valuesList = this.weekDays;
        break;
      case 4:
      case 5:
      case 6:
        this.valuesList = Array(31).fill(1).map((_, i) => <property>{ id: i + 1, name: (i + 1).toString() });
        this.valuesList.push({ id: 32, name: "Last day of month" });
        break;
      case 7:
        this.valuesList = this.months;
        break;
      default:
        this.valuesList = [];
        break;
    }

    this.completed = this.disableValueSelect || this.gridItem.setup.period[this.model + 'Value'] != null;
    this.completed = this.completed && isPeriodCorrect;
    this.completedChange.emit(this.completed);
    this.addToDashboardChange?.emit();
  }

  checkPeriodLength(): boolean {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    let from = this.calculatedDate("from");
    let to = this.calculatedDate("to");
    if ((this.model == "from" && to == null) || (this.model == "to" && from == null)) {
      return true;
    }
    if ((this.model == "from" && from == null) || (this.model == "to" && to == null)) {
      return false;
    }
    const days = Math.floor((to.getTime() - from.getTime()) / _MS_PER_DAY);
    if (this.reportMaxDays != 0 && days > this.reportMaxDays) {
      setTimeout(() => {
        if (this.gridItem.setup.period[this.model + 'DateType'] < 2) {
          this.gridItem.setup.period[this.model + 'DateType'] = null;
        } else {
          this.gridItem.setup.period[this.model + 'Value'] = null;
        }
      }, 0)
      this.store.dispatch(
        mainActions.addMessage({
          message: new ErrorMessage(
            this.i18nextPipe.transform('messages:Period is too long'),
            this.i18nextPipe.transform("messages:The maximum time interval for this report is (in days)") + ": " + this.reportMaxDays)
        }));
      return false;
    }

    from = new Date(from.getTime() + this.getTimeInMilisec("from"));
    to = new Date(to.getTime() + this.getTimeInMilisec("to"));
    if ((to.getTime() <= from.getTime()) && this.isExportDialog !== true) {
      setTimeout(() => {
        if (this.gridItem.setup.period[this.model + 'DateType'] < 2) {
          this.gridItem.setup.period[this.model + 'DateType'] = null;
        } else {
          this.gridItem.setup.period[this.model + 'Value'] = null;
        }
      }, 0)
      this.store.dispatch(
        mainActions.addMessage({
          message: new ErrorMessage(
            this.i18nextPipe.transform('messages:Invalid date range selection'),
            this.i18nextPipe.transform('messages:"To" field should be a date-time later than the "From" selection'))
        }));
      return false;
    }

    return true;
  }

  SelectValueChange($event) {
    this.setTimers($event);
    this.completed = this.checkPeriodLength();
    this.completedChange.emit(this.completed);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled && !changes.disabled.firstChange && changes.disabled.currentValue) {
      this.gridItem.setup.period[this.model + 'DateType'] = null;
      this.gridItem.setup.period[this.model + 'Value'] = null;
      this.gridItem.setup.period[this.model + 'Time'] = '00:00';
    }

    if (changes.disabled && !changes.disabled.firstChange && !changes.disabled.currentValue) {
      this.gridItem.setup.period[this.model + 'Time'] = this.model == "to" ? '24:00' : '00:00';
      this.completedChange.emit(false);
    }

    if (changes.gridItem) {
      this.SetValueSelct(changes.gridItem.currentValue.setup.period[this.model + 'DateType']);
    }
  }

  ngOnInit(): void {
    this.oppositeModel = this.model === "to" ? "from" : "to";
    if (this.gridItem.setup.period[this.model + 'Time'] == "23:59") {
      this.gridItem.setup.period[this.model + 'Time'] = "24:00"
    }

    this.notifier?.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
      let event: property = {
        id: value,
        name: ""
      }

      this.SelectDayTypesChange(event);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setTimers(event): void {
    let value = this.gridItem.setup.period[this.model + 'Time'];
    if (value.split(":")[0] === "24") this.gridItem.setup.period[this.model + 'Time'] = "24:00";

    if (this.gridItem.setup.period.type === 1) {
      if (this.model === "from") {
        let fromHours = value.split(":")[0];
        let fromMinutes = value.split(":")[1];
        let toHours = this.gridItem.setup.period['toTime'].split(":")[0];
        let toMinutes = this.gridItem.setup.period['toTime'].split(":")[1];
  
        if (+fromHours === +toHours && +fromMinutes > +toMinutes) {
          this.gridItem.setup.period[this.model + 'Time'] = `${fromHours}:${toMinutes}`;
        }
      }

      if (this.model === "to") {
        let toHours = value.split(":")[0];
        let toMinutes = value.split(":")[1];
        let fromHours = this.gridItem.setup.period['fromTime'].split(":")[0];
        let fromMinutes = this.gridItem.setup.period['fromTime'].split(":")[1];

        if (toHours === fromHours && fromMinutes > toMinutes) {
          this.gridItem.setup.period[this.model + 'Time'] = `${toHours}:${fromMinutes}`;
        }
      }
    }
  }

  calculatedDate(model): Date {
    if (!this.gridItem.setup?.period) {
      return undefined;
    }
    const period = this.gridItem.setup.period;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (period[model + 'DateType'] === 0) {
      return today;
    }
    if (period[model + 'DateType'] === 1) {
      return new Date(today.setDate(today.getDate() - 1));
    }
    if (period[model + 'Value'] == null) {
      return undefined;
    }
    if (period[model + 'DateType'] === 2) {
      return new Date(today.setDate(today.getDate() - today.getDay() + period[model + 'Value'] + 1));
    }
    if (period[model + 'DateType'] === 3) {
      return new Date(today.setDate(today.getDate() - today.getDay() + period[model + 'Value'] - 6));
    }
    if (period[model + 'DateType'] === 4) {
      let day = period[model + 'Value'];
      if (day == 32) {
        day = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      }
      return new Date(today.getFullYear(), today.getMonth(), day);
    }
    if (period[model + 'DateType'] === 5) {
      const lastMonth = new Date(today);
      lastMonth.setDate(0);
      let day = period[model + 'Value'];
      if (day == 32) {
        day = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
      }
      return new Date(lastMonth.getFullYear(), lastMonth.getMonth(), day);
    }
    if (period[model + 'DateType'] === 6) {
      return new Date(today.getFullYear(), 0, period[model + 'Value'] == 32 ? 31 : period[model + 'Value']);
    }
  }

  getTimeInMilisec(model: string): number {
    if (this.gridItem.setup.period[model + "Time"] == null) return 0;
    let timeStrArr = this.gridItem.setup.period[model + "Time"].split(":");
    return ((parseInt(timeStrArr[0]) | 0) * 60 + (parseInt(timeStrArr[1]) | 0)) * 60 * 1000;
  }
}
